import { Component, Input, OnDestroy } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { take } from 'rxjs/operators';
import { ActiveRoutesDetail } from '../active-routes-detail/interfaces/ActiveRoutesDetail.interface';

@Component({
    selector: 'app-pdf-viewer-modal',
    templateUrl: './pdf-viewer-modal.component.html',
    styleUrls: ['./pdf-viewer-modal.component.scss'],
})
export class PdfViewerModalComponent implements OnDestroy {

    @Input() pdfName: string = '';
    @Input() downloadLabelKey: string = 'SERVICE_DETAILS.REPORT_CARD.1';
    @Input() showTerms: boolean = false;

    @Input() reportData?: ActiveRoutesDetail;
    @Input() reportOrderHash?: string;
    @Input() isEconomicReport: boolean = false;
    @Input() downloadFn?: () => void;

    pdfSrc: Uint8Array | null = null;
    isLoading: boolean = false;
    loadError: boolean = false;
    totalPages: number = 0;
    currentPage: number = 1;
    pages: number[] = [];
    thumbnails: string[] = [];

    private _rawBlob: Blob | null = null;
    private _destroyed = false;

    constructor(public bsModalRef: BsModalRef) { }
    @Input() set pdfBlob(blob: Blob) {
        if (!blob) return;
        this.loadError = false;
        this.isLoading = true;
        blob.arrayBuffer().then(buf => {
            if (this._destroyed) return;
            this._rawBlob = blob;
            this.pdfSrc = new Uint8Array(buf);
        });
    }

    @Input() set pdfUrl(url: string) {
        if (!url) return;
        this.loadError = false;
        this.isLoading = true;

        fetch(url, { method: 'GET', mode: 'cors' })
            .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.arrayBuffer(); })
            .then(buf => {
                if (this._destroyed) return;
                this._rawBlob = new Blob([buf], { type: 'application/pdf' });
                this.pdfSrc = new Uint8Array(buf);
            })
            .catch(e => {
                if (this._destroyed) return;
                console.error('[PdfViewerModal] fetch error', e);
                this.loadError = true;
                this.isLoading = false;
            });
    }

    onPdfLoaded(pdf: any): void {
        this.totalPages = pdf.numPages as number;
        this.pages = Array.from({ length: this.totalPages }, (_, i) => i + 1);
        this.thumbnails = new Array(this.totalPages).fill('');
        this.currentPage = 1;
        this.isLoading = false;
        this.loadError = false;
        this.generateThumbnails(pdf);
    }

    private async generateThumbnails(pdf: any): Promise<void> {
        for (let i = 1; i <= pdf.numPages; i++) {
            if (this._destroyed) break;
            try {
                const page = await pdf.getPage(i);
                const viewport = page.getViewport({ scale: 0.35 });
                const canvas = document.createElement('canvas');
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                const ctx = canvas.getContext('2d')!;
                await page.render({ canvasContext: ctx, viewport }).promise;
                this.thumbnails[i - 1] = canvas.toDataURL('image/jpeg', 0.75);
            } catch (e) {
                console.warn('[PdfViewerModal] thumbnail error page', i, e);
            }
        }
    }

    goToPage(page: number): void {
        this.currentPage = page;
    }

    onPdfError(error: unknown): void {
        console.error('[PdfViewerModal] render error', error);
        this.loadError = true;
        this.isLoading = false;
    }


    download(): void {
        if (this.downloadFn) {
            const fn = this.downloadFn;
            this.bsModalRef.onHide!.pipe(take(1)).subscribe(() => fn());
            this.bsModalRef.hide();
            return;
        }
        const blob = this._rawBlob;
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = this.pdfName || `document-${Date.now()}.pdf`;
        anchor.style.display = 'none';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    }

    close(): void {
        this.bsModalRef.hide();
    }

    ngOnDestroy(): void {
        this._destroyed = true;
        this.pdfSrc = null;
        this._rawBlob = null;
    }
}
